import React from 'react';
import { NavigateNext } from '@mui/icons-material';
import { Breadcrumbs, FormControl, MenuItem, Select, SxProps, Typography } from '@mui/material';
import { useMerchant, isMerchantIdParentPickerRow } from '../../contexts/MerchantContext';
import { useSwitchMerchant } from '../../hooks/useSwitchMerchant';
import { Button } from './Button/Button';

type Props = {
  sx?: SxProps;
};

const HierarchyBreadcrumbs = ({ sx }: Props) => {
  const switchMerchant = useSwitchMerchant();
  const {
    merchantId,
    merchantName,
    directLineage,
    isChildMerchantOnlyUser,
    parentMerchants,
    parentMerchantsTotalRecords,
    requiresParentSelection,
    partnerMerchantId,
    pendingChildMerchantLandingAfterParentPick,
  } = useMerchant();

  /** Parent switcher instead of crumbs when on child-only parent-org / landing context. */
  const merchantIdIsParentPickerRow = isMerchantIdParentPickerRow(merchantId, parentMerchants);

  const isParentOrgContextForSwitcher =
    pendingChildMerchantLandingAfterParentPick ||
    merchantIdIsParentPickerRow ||
    (partnerMerchantId !== undefined && (partnerMerchantId === null || partnerMerchantId === ''));

  const parentCountForUi = parentMerchantsTotalRecords ?? parentMerchants?.length ?? 0;

  const showRootParentDropdown =
    isChildMerchantOnlyUser &&
    !requiresParentSelection &&
    isParentOrgContextForSwitcher &&
    !!parentMerchants &&
    parentCountForUi > 1;

  const rawBreadcrumbs: { name: string; merchantId: string; hasAccess: boolean }[] =
    !merchantName || !merchantId
      ? []
      : [
          ...(directLineage?.toReversed() ?? []),
          { name: merchantName, merchantId, hasAccess: true },
        ];

  const breadcrumbs = isChildMerchantOnlyUser
    ? rawBreadcrumbs.filter((b) => b.hasAccess || (b.name?.trim().length ?? 0) > 0)
    : rawBreadcrumbs;

  if (requiresParentSelection && isChildMerchantOnlyUser) {
    return null;
  }

  const inlineCrumbs = showRootParentDropdown ? [] : breadcrumbs;

  const hiddenWithoutDropdown = inlineCrumbs.length <= 1 && !showRootParentDropdown;

  const handleBreadcrumbNavigate = (targetMerchantId: string, isLastCrumb: boolean) => {
    if (isLastCrumb || targetMerchantId === merchantId) {
      return;
    }
    switchMerchant(targetMerchantId);
  };

  return (
    <Breadcrumbs
      separator={<NavigateNext fontSize="small" sx={{ color: 'common.black' }} />}
      aria-label="Merchant Hierarchy"
      sx={{
        height: '24px',
        visibility: hiddenWithoutDropdown ? 'hidden' : 'visible',
        ...sx,
      }}
    >
      {showRootParentDropdown ? (
        <FormControl size="small" variant="standard" sx={{ minWidth: 120, mr: 0.5 }}>
          <Select
            aria-label="Switch parent merchant"
            value={merchantId}
            onChange={(e) => {
              const id = e.target.value as string;
              if (id) {
                switchMerchant(id);
              }
            }}
            renderValue={() => (
              <Typography component="span" sx={{ fontSize: '14px', fontWeight: 500 }}>
                {parentMerchants!.find((p) => p.merchantId === merchantId)?.name ?? merchantName}
              </Typography>
            )}
            sx={{
              '&:before': { borderBottom: 'none' },
              '&:after': { borderBottom: 'none' },
              '& .MuiSelect-select': { py: 0, display: 'flex', alignItems: 'center' },
            }}
          >
            {parentMerchants!.map((p) => (
              <MenuItem key={p.merchantId} value={p.merchantId}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
      {inlineCrumbs.map((breadcrumb, index) => (
        <Button
          variant="text"
          key={breadcrumb.merchantId}
          text={breadcrumb.name}
          disabled={!breadcrumb.hasAccess}
          onClick={() =>
            handleBreadcrumbNavigate(breadcrumb.merchantId, index === inlineCrumbs.length - 1)
          }
          sx={{
            px: 0,
            py: 0,
            minWidth: 'fit-content',
            '& p': {
              visibility: 'inherit',
              fontWeight:
                index === inlineCrumbs.length - 1 && !showRootParentDropdown ? 600 : 'normal',
              '&:hover': {
                textDecoration: 'underline',
              },
            },
            '&.Mui-disabled': {
              '& p': {
                color: 'grey.500',
                textDecoration: 'none',
              },
            },
          }}
        />
      ))}
    </Breadcrumbs>
  );
};

export default HierarchyBreadcrumbs;
